import { withTranslation } from 'react-i18next';
import moment from 'moment';

import DataTable from '../../../../components/Datatable';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { ASSUMED_PROVISIONAL_HOURS } from '../hours';
import { boundaryColumns } from '../keys';
import { boundaryRows } from '../rows';

/**
 * Block 01 — how fresh the data is, and where this report stops trusting it.
 *
 * First on the page because every number below it is qualified by one of these four lines. An
 * hourly report read without them is read as live traffic, which it is not: the newest hour here
 * is behind by however long Search Console's hourly pipeline takes, and the last hours of it are
 * still being written.
 *
 * The "first incomplete hour" row is the one that has to admit what it is. Search Console answers
 * that question in `metadata.first_incomplete_hour`, but the Cloud Function this app reads
 * through hands back the response's rows and drops the metadata, so the boundary here is this
 * report's own assumption about the trailing hours. Saying "assumed" costs a reader nothing and
 * saves them from treating a guess as Google's answer.
 */
function Freshness({ t, scope, window, baselineWindow, hours, baselineReturned, fetchedAt }) {
    const columns = boundaryColumns(t, { comparing: scope.comparing });
    const rows = boundaryRows(t, { scope, window, baselineWindow, hours, baselineReturned });

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.latest_hours.freshness.title')}</p>
                    <p className="gsc-tool-sub">{t('tools.latest_hours.freshness.description')}</p>
                </div>

                {!!fetchedAt && (
                    <p className="gsc-tool-sub">
                        {t('tools.gsc.coverage.fetched', {
                            time: moment(fetchedAt).format('DD MMM YYYY, HH:mm'),
                        })}
                    </p>
                )}
            </div>

            <div className="card-body">
                <DataTable
                    nodes={rows}
                    COLUMNS={columns}
                    sortFns={getSortKeys(columns)}
                    additionalStyles={setAdditionalStyles(columns)}
                    onlyTable
                    notUseDefaultStyle
                    customContainerStyles="gsc-tool-rules-table"
                />

                <p className="gsc-tool-table-note">
                    {t('tools.latest_hours.freshness.assumed_note', {
                        hours: ASSUMED_PROVISIONAL_HOURS,
                    })}
                </p>
                <p className="gsc-tool-table-note">
                    {t('tools.latest_hours.freshness.not_realtime')}
                </p>
            </div>
        </div>
    );
}

export default withTranslation()(Freshness);
